import { H } from 'highlight.run';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  SigninRequest,
  SignupRequest,
  UserPatchRequest,
} from '@interfaces/auth.interface';

import {
  useLogoutMutation,
  useSigninMutation,
  useSignupMutation,
} from '@api/auth.api';
import api from '@api/index';
import { usePatchCurrentUserMutation } from '@api/users.api';

import {
  logout as logoutAction,
  selectCurrentUser,
  setCredentials,
} from '@slices/auth.slice';

const useAuth = () => {
  const dispatch = useDispatch();

  const user = useSelector(selectCurrentUser);

  const [signinMutation, { isLoading: isLoadingSignin }] = useSigninMutation();
  const [signupMutation, { isLoading: isLoadingSignup }] = useSignupMutation();
  const [logoutMutation, { isLoading: isLoadingLogout }] = useLogoutMutation();
  const [patchMutation, { isLoading: isLoadingPatch }] =
    usePatchCurrentUserMutation();

  const signin = useCallback(
    async (data: SigninRequest) => {
      const user = await signinMutation(data).unwrap();

      if (user) {
        H.identify(user.email, {
          id: user.id,
          name: user.name,
        });
      }

      dispatch(setCredentials({ user }));
    },
    [dispatch, signinMutation],
  );

  const signup = useCallback(
    async (data: SignupRequest) => {
      await signupMutation(data).unwrap();
    },
    [signupMutation],
  );

  const logout = useCallback(async () => {
    await logoutMutation().unwrap();

    dispatch(api.util.resetApiState());
    dispatch(logoutAction());
  }, [dispatch, logoutMutation]);

  const patch = useCallback(
    async (body: UserPatchRequest) => {
      const patchedUser = await patchMutation(body).unwrap();
      const updatedUser = {
        ...user,
        ...patchedUser,
      };
      dispatch(setCredentials({ user: updatedUser }));
    },
    [dispatch, patchMutation, user],
  );

  return useMemo(
    () => ({
      user,
      isAuthenticated: user !== null,
      signin,
      signup,
      patch,
      logout,
      isLoading:
        isLoadingSignin || isLoadingSignup || isLoadingLogout || isLoadingPatch,
    }),
    [
      user,
      signin,
      signup,
      patch,
      logout,
      isLoadingSignin,
      isLoadingSignup,
      isLoadingLogout,
      isLoadingPatch,
    ],
  );
};

export default useAuth;
